import React from "react";
import { formatMoney, formatMoneyMajor, formatMoneyWithBase, getBaseCurrency } from "../../core/currency";
import "./Money.scss";

// Fontaine M20 — Money: the ONE way an amount is rendered. Tabular numerals, currency-aware
// (src/core/currency.js is the formatter), negatives, POR (price on request), optional base-currency
// bracket (Q9 rule) and a muted/strong tone. Display only — never mutates stored values.

/**
 * @param {number|string|null} value              amount in MINOR units (pence) by default
 * @param {boolean} [major]                       value is already in major units
 * @param {string} [currency]                     ISO code; defaults to the store base
 * @param {number} [rateToBase]                   when given and currency ≠ base → "$1,000 (HK$7,810)"
 * @param {boolean} [por]                         render "Price on request" (also when value === 0 && porOnZero)
 * @param {boolean} [porOnZero]
 * @param {number} [decimals]                     override fraction digits (0 for whole-unit prices)
 * @param {"default"|"muted"|"strong"|"success"|"danger"} [tone]
 * @param {"sm"|"md"|"lg"|"xl"} [size="md"]
 * @param {boolean} [signed]                      always show + / −
 */
export default function Money({ value, major = false, currency, rateToBase, por = false, porOnZero = false, decimals, tone = "default", size = "md", signed = false, className = "", ...rest }) {
  const cur = currency || getBaseCurrency();
  const n = value == null || value === "" ? null : Number(value);
  const isPor = por || (porOnZero && n === 0);
  const classes = ["ft-money", `ft-money--${size}`, `ft-money--${tone}`, n != null && n < 0 ? "is-negative" : "", isPor ? "is-por" : "", className].filter(Boolean).join(" ");

  if (isPor) {
    return <span className={classes} {...rest}>Price on request</span>;
  }
  if (n == null || Number.isNaN(n)) {
    return <span className={`${classes} is-empty`} aria-label="No amount" {...rest}>—</span>;
  }

  let text;
  const opts = { currency: cur, decimals };
  if (rateToBase != null && !major) text = formatMoneyWithBase(n, cur, rateToBase, opts);
  else text = major ? formatMoneyMajor(n, opts) : formatMoney(n, opts);
  if (signed && n > 0) text = `+${text}`;
  // formatter renders "-" ; typography wants a true minus
  text = text.replace(/^-/, "−");

  return (
    <span className={classes} data-currency={cur} {...rest}>
      {text}
    </span>
  );
}
